import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  signOut,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  updateUserAttribute,
  confirmUserAttribute,
  fetchUserAttributes
} from 'aws-amplify/auth';

import { BehaviorSubject } from 'rxjs';
import { clearTokenCache } from './ProxyApi';
import { broadcastLogout } from '../hooks/useLogoutListener';
import proxyapi from './ProxyApi';

class AuthError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

export class AuthService {
  authStatusSubject = new BehaviorSubject(false);
  authStatus$ = this.authStatusSubject.asObservable();

  constructor() {
    this.checkAuthStatus();
  }

  passwordMeetsRequirements(password) {
    const minLength = 8;
    const hasNumber = /\d/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return (
      password.length >= minLength &&
      hasNumber &&
      hasUpperCase &&
      hasLowerCase &&
      hasSpecialChar
    );
  }

  async signUp(email, password) {
    if (!this.passwordMeetsRequirements(password)) {
      throw new Error(
        'Password must be at least 8 characters long and contain at least one number, ' +
        'one uppercase letter, one lowercase letter, and one special character'
      );
    }

    try {
      const signUpResponse = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email
          }
        }
      });
      
      return {
        isSignUpComplete: signUpResponse.isSignUpComplete,
        userId: signUpResponse.userId,
        nextStep: signUpResponse.nextStep
      };
    } catch (error) {
      throw new AuthError(error.code || 'SignUpError', error.message);
    }
  }

  async confirmSignUp(email, code) {
    try {
      const result = await confirmSignUp({
        username: email,
        confirmationCode: code
      });
      
      return result;
    } catch (error) {
      throw new AuthError(error.code || 'ConfirmationError', error.message);
    }
  }

  async resendConfirmationCode(email) {
    try {
      return await resendSignUpCode({
        username: email
      });
    } catch (error) {
      throw new AuthError(error.code || 'ResendCodeError', error.message);
    }
  }

  async signIn(email, password = null) {
    try {
      const user = await signIn({
        username: email,
        password
      });
      this.authStatusSubject.next(true);
      return user;
    } catch (error) {
      throw new AuthError(error.code || 'SignInError', error.message);
    }
  }

  async signOut() {
    try {
      console.log('AuthService: Starting signOut process');

      // First broadcast the logout to other tabs
      broadcastLogout();

      // Clear token cache
      clearTokenCache();

      // Then sign out from Amplify
      await signOut();

      // Finally notify subscribers in the same tab
      this.authStatusSubject.next(false);

      console.log('AuthService: Completed signOut process');
      return { success: true };
    } catch (error) {
      console.error('AuthService: Error during signOut', error);

      // Still clear cache and notify subscribers even on error
      clearTokenCache();
      this.authStatusSubject.next(false);

      throw new AuthError(error.code || 'SignOutError', error.message);
    }
  }

  async checkAuthStatus() {
    try {
      await getCurrentUser();
      this.authStatusSubject.next(true);
    } catch (error) {
      this.authStatusSubject.next(false);
    }
  }

  async saveUserInfo(email) {
    try {
      await proxyapi.post('/api/v1/users', { email });
    } catch (error) {
      console.error('Error saving user info:', error);
      throw new Error('Failed to save user information');
    }
  }

  async forgotPassword(email) {
    try {
      const result = await resetPassword({
        username: email
      });
      return result;
    } catch (error) {
      throw new AuthError(error.code || 'ForgotPasswordError', error.message);
    }
  }
  
  async confirmPassword(email, code, newPassword) {
    if (!this.passwordMeetsRequirements(newPassword)) {
      throw new Error(
        'Password must be at least 8 characters long and contain at least one number, ' +
        'one uppercase letter, one lowercase letter, and one special character'
      );
    }
  
    try {
      const result = await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
      return result;
    } catch (error) {
      throw new AuthError(error.code || 'ConfirmPasswordError', error.message);
    }
  }

async updatePassword(oldPassword, newPassword) {
  if (!this.passwordMeetsRequirements(newPassword)) {
    throw new Error(
      'Password must be at least 8 characters long and contain at least one number, ' +
      'one uppercase letter, one lowercase letter, and one special character'
    );
  }

  try {
    await updatePassword({
      oldPassword,
      newPassword
    });
    return { success: true };
  } catch (error) {
    throw new AuthError(error.code || 'UpdatePasswordError', error.message);
  }
}

// Add these methods to the AuthService class in auth.js

async fetchUserAttributes() {
  try {
    const attributes = await fetchUserAttributes();
    return attributes;
  } catch (error) {
    throw new AuthError(error.code || 'FetchAttributesError', error.message);
  }
}

async updateEmail(newEmail) {
  try {
    const output = await updateUserAttribute({
      userAttribute: {
        attributeKey: 'email',
        value: newEmail
      }
    });
    return output;
  } catch (error) {
    throw new AuthError(error.code || 'UpdateEmailError', error.message);
  }
}

async confirmEmailUpdate(confirmationCode) {
  try {
    await confirmUserAttribute({
      userAttributeKey: 'email',
      confirmationCode
    });
    return { success: true };
  } catch (error) {
    throw new AuthError(error.code || 'ConfirmEmailError', error.message);
  }
}

async silentSignOut() {
  try {
    clearTokenCache();
    await signOut();
    this.authStatusSubject.next(false);
    return { success: true };
  } catch (error) {
    this.authStatusSubject.next(false);
    throw new AuthError(error.code || 'SilentSignOutError', error.message);
  }
}

async confirmAndCreateUser(email, code, userId) {
  try {
    const resp = await proxyapi.post('/api/v1/auth/confirm', {
      email,
      code,
      userId
    });
    return resp.data;
  } catch (err) {
    throw new AuthError(err.response?.data?.code || 'ConfirmError', err.response?.data?.message || err.message);
  }
}

}
